import { useEffect } from 'react'

import { useIsEditMode } from '@/hooks/useIsEditMode'
import { useLightMode } from '@/hooks/useLightMode'
import { useDispatch, useSelector } from 'react-redux'

import { Box, useDisclosure, useToast } from '@chakra-ui/react'

import { LockedIcon, SideNavItem, UnlockedIcon } from '@hivewatch/dark'

import {
  selectConfirmEditMode,
  selectSideNavOpen,
  toggleEditMode,
} from '@/redux/ui/uiSlice'
import { mixpanel } from '@/utils/analytics'

import { ConfirmEditModeDialog } from '../EditMode'

/**
 * The original Edit Mode toggle, at the bottom of the Side Bar.
 *
 * Only rendered while the `removeEditMode` feature flag is off. With the flag
 * on, Edit Mode narrows to the Floor Plan and its toggle moves into that
 * header — see `EditModeToggle` in `@/features/floor-plan`.
 *
 * @deprecated Deleted along with the rest of Edit Mode once `removeEditMode`
 * is fully rolled out.
 */
export const SideBarEditMode = () => {
  const toast = useToast()
  const id = 'edit-mode-toast'
  const dispatch = useDispatch()
  // Keep as old Edit Mode check!
  const isEditMode = useIsEditMode()
  const isSideNavOpen = useSelector(selectSideNavOpen)
  const hasConfirmedEditMode = useSelector(selectConfirmEditMode)
  const colorScheme = useLightMode()
  const { isOpen, onOpen, onClose } = useDisclosure()

  const handleToggleEditMode = () => {
    if (!isEditMode && !toast.isActive(id)) {
      toast({
        id,
        position: 'top',
        duration: 2000,
        render: () => (
          <Box
            bg='#fff'
            borderRadius='5px'
            boxShadow='3px 3px 5px 6px rgba(0, 0, 0, 0.2)'
            color='#353849'
            fontWeight='bold'
            letterSpacing='-0.4px'
            mt='75px'
            py='2'
            textAlign='center'
          >
            Now in edit mode <span>🔏</span>
          </Box>
        ),
      })
    }
    dispatch(toggleEditMode())
  }

  const togglingEditMode = () => {
    handleToggleEditMode()
    const date = new Date().toUTCString()
    mixpanel.track('Edit Mode Pressed', {
      user_id: '1',
      facility_id: '1',
      event_at: date,
      total_edit_clicks_no_editmode: '1',
    })
  }

  const handleOnClick = () =>
    hasConfirmedEditMode ? togglingEditMode() : onOpen()

  useEffect(() => {
    if (isEditMode) {
      const date = new Date().toUTCString()
      mixpanel.track('Enters Edit Mode', {
        user_id: '1',
        facility_id: '1',
        event_at: date,
        total_edit_clicks_no_editmode: '1',
      })
    }
  }, [isEditMode])

  return (
    <>
      <ConfirmEditModeDialog isOpen={isOpen} onClose={onClose} />
      <SideNavItem
        colorScheme={colorScheme}
        data-testid='SideBarEditMode'
        icon={isEditMode ? UnlockedIcon : LockedIcon}
        isActive={isEditMode}
        isExpanded={isSideNavOpen}
        label='Edit Mode'
        onClick={handleOnClick}
      />
    </>
  )
}
